import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { ColDef, GridApi, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { defineMessage, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import Trip from "@mapmycustomers/shared/types/entity/Trip";
import { AnyEntity } from "@mapmycustomers/shared/types/entity/types";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import EditableNoteCellRenderer from "@app/component/grid/gridComponents/cellRenderers/EditableNoteCellRenderer";
import TripDateCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TripDateCellRenderer";
import TripLocationCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TripLocationCellRenderer";
import TripTypeCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TripTypeCellRenderer";
import UserChipCellRenderer from "@app/component/grid/gridComponents/cellRenderers/UserChipCellRenderer";
import AgGridListView from "@app/component/view/ListView/AgGridListView";
import AgGridListViewServerSideGrid from "@app/component/view/ListView/AgGridListViewServerSideGrid";
import AgGridListViewPagination from "@app/component/view/ListView/components/Pagination/AgGridListViewPagination";
import mileageTrackingAnalytics from "@app/scene/reports/components/MileageTracking/mileageTrackingAnalytics";
import {
  fetchTripList,
  initializeTripListView,
  updateTrip,
} from "@app/scene/reports/store/actions";
import {
  getMileageTrackingViewState,
  getTotalFilteredTripRecordsCount,
  getTotalTripRecordsCount,
  getTripGridColumnDefs,
  isTripListInitialized,
  isTripListLoading,
} from "@app/scene/reports/store/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import { subscribe, UiSyncListener, unsubscribe } from "@app/store/uiSync/actions";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import tripFieldModel from "@app/util/fieldModel/tripFieldModel";

import BaseScreenProps from "../BaseScreenProps";

import TripPreview from "./components/preview";
import Stats from "./components/Stats";
import Toolbar from "./components/Toolbar";
import TripSelectBar from "./components/TripSelectBar";
import styles from "./MileageTrackingPage.module.scss";

export const mileageTrackerTitle = defineMessage({
  id: "reports.mileageTracking.title",
  defaultMessage: "Mileage Tracker",
  description: "Mileage Tracking page title",
});

interface Props extends BaseScreenProps {
  columnDefs: ColDef[];
  initialized: boolean;
  listFetcher: typeof fetchTripList.request;
  listViewState: ViewState;
  loading: boolean;
  onEntityView: typeof showEntityView;
  onInitialize: () => void;
  onUpdateTrip?: (payload: { entity: Trip; onSuccess?: () => void }) => void;
  subscribe: typeof subscribe;
  totalFilteredRecords: number;
  totalRecords: number;
  unsubscribe: typeof unsubscribe;
}

const MileageTrackingPage: React.FC<Props> = ({
  columnDefs,
  initialized,
  listFetcher,
  listViewState,
  loading,
  onEntityView,
  onInitialize,
  onUpdateTrip,
  subscribe,
  totalFilteredRecords,
  totalRecords,
  unsubscribe,
}) => {
  const intl = useIntl();

  useEffect(() => {
    mileageTrackingAnalytics.initiated();
    onInitialize();
  }, [onInitialize]);

  const gridApi = useRef<GridApi | undefined>(undefined);
  const handleGridReady = useCallback((event: GridReadyEvent) => (gridApi.current = event.api), []);

  const [selectedTrips, setSelectedTrips] = useState<Set<Trip>>(new Set());
  const handleRowSelectionChanged = useCallback(
    ({ api }: RowSelectedEvent) => {
      setSelectedTrips(new Set(api.getSelectedRows()));
    },
    [setSelectedTrips]
  );
  useEffect(() => {
    // reset selected rows when filter changes
    gridApi.current?.deselectAll();
  }, [listViewState.filter]);

  const handleReloadData = useCallback(() => {
    gridApi.current?.deselectAll();
    const sortModel = gridApi.current?.getSortModel();
    if (
      sortModel &&
      sortModel.length &&
      sortModel[0].colId === "updatedAt" &&
      sortModel[0].sort === "desc"
    ) {
      // force reload and go to the first page if we currently sort data by updatedAt desc
      gridApi.current?.onFilterChanged();
    } else {
      // otherwise, reload the current page
      gridApi.current?.onSortChanged();
    }
  }, []);

  useEffect(() => {
    const listener: UiSyncListener = {
      onAdd: () => handleReloadData(),
      onDelete: () => handleReloadData(),
      onUpdate: (entityType: EntityType, entities: AnyEntity[]) => {
        // a special case when just one trip is updated, a quick grid update
        if (entities.length === 1) {
          let found = false;
          gridApi.current?.forEachNode((node) => {
            if ((node.data as Trip)?.id === entities[0].id) {
              node.updateData(entities[0] as Trip);
              found = true;
            }
          });
          // if was updated - exit, otherwise proceed to full grid reload
          if (found) {
            return;
          }
        }
        handleReloadData();
      },
    };
    subscribe({ entityTypes: [EntityType.TRIP], listener });
    return () => {
      unsubscribe({ entityTypes: [EntityType.TRIP], listener });
    };
  }, [handleReloadData, subscribe, unsubscribe]);

  const [selectedTripId, setSelectedTripId] = useState<Trip["id"] | undefined>();
  const handleViewClick = useCallback((trip: Trip) => {
    setSelectedTripId(trip.id);
    mileageTrackingAnalytics.clicked(["A report to view"], { trip });
  }, []);
  const handleHidePreview = useCallback(() => setSelectedTripId(undefined), []);

  return (
    <AnalyticsContext.Provider value={mileageTrackingAnalytics}>
      {initialized && (
        <AgGridListView<Trip>
          columnDefs={columnDefs}
          entityType={EntityType.TRIP}
          fieldModel={tripFieldModel}
          listFetcher={listFetcher}
          listViewState={listViewState}
          totalRecords={totalRecords}
        >
          <section className={styles.listViewGrid}>
            <div className={styles.title}>{intl.formatMessage(mileageTrackerTitle)}</div>
            <TripSelectBar onReloadData={handleReloadData} selectedTrips={selectedTrips} />
            <Toolbar columns={listViewState.columns} totalFilteredRecords={totalFilteredRecords} />
            <Stats />
            <AgGridListViewServerSideGrid
              context={{
                onEntityView,
                onSaveNote: onUpdateTrip,
                onView: handleViewClick,
              }}
              enableCellTextSelection // Note: When this is set to true, the clipboard service is disabled.
              frameworkComponents={{
                editableNoteCellRenderer: EditableNoteCellRenderer,
                tripDateCellRenderer: TripDateCellRenderer,
                tripLocationCellRenderer: TripLocationCellRenderer,
                tripTypeCellRenderer: TripTypeCellRenderer,
                userChipCellRenderer: UserChipCellRenderer,
              }}
              modules={[MenuModule]}
              onGridReady={handleGridReady}
              onSelectionChanged={handleRowSelectionChanged}
              paginationPageSize={listViewState.range.endRow - listViewState.range.startRow}
              showNoRows={!totalFilteredRecords && !loading}
              suppressPaginationPanel
            />
          </section>
          <AgGridListViewPagination showSizeChanger totalRecords={totalFilteredRecords} />
        </AgGridListView>
      )}
      {selectedTripId && (
        <ExtendedAnalytics keyPath="Drive Record Panel">
          <TripPreview onHide={handleHidePreview} tripId={selectedTripId} />
        </ExtendedAnalytics>
      )}
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  columnDefs: getTripGridColumnDefs(state)(false),
  initialized: isTripListInitialized(state),
  listViewState: getMileageTrackingViewState(state),
  loading: isTripListLoading(state),
  totalFilteredRecords: getTotalFilteredTripRecordsCount(state),
  totalRecords: getTotalTripRecordsCount(state),
});

const mapDispatchToProps = {
  listFetcher: fetchTripList.request,
  onEntityView: showEntityView,
  onInitialize: initializeTripListView.request,
  onUpdateTrip: updateTrip.request,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(MileageTrackingPage);
